/**
 * Motor de cálculo da apuração semanal — port fiel do protótipo
 * "Apuração Semanal PLcom.html" (mesma lógica de efeitos manuais,
 * carry-over de saldo credor entre semanas e consolidação do mês).
 */
import { LANC_MES, TRIBUTOS, TRIBUTO_NOME, BLOCOS_MANUAIS } from "../mockDataSemanal";

const sum = (a) => a.reduce((s, x) => s + x, 0);

const zeroTrib = () => ({ icms: 0, ipi: 0, pis: 0, cofins: 0 });

/**
 * Efeito dos lançamentos manuais de uma semana.
 * lancamentos: [{ blocoId, tributo, valor }]
 * Retorna deb/cred por tributo, porBloco e total
 * (+ aumenta o total a recolher, − reduz).
 */
export function calcEfeitos(lancamentos) {
  const deb = zeroTrib();
  const cred = zeroTrib();
  const porBloco = {};

  BLOCOS_MANUAIS.forEach((b) => {
    let d = 0;
    b.campos.forEach((f) => {
      const [trib, lado, sinal] = f;
      const v = (lancamentos || [])
        .filter((l) => l.blocoId === b.id && l.tributo === trib)
        .reduce((s, l) => s + l.valor, 0);
      if (lado === "deb") {
        deb[trib] += sinal * v;
        d += sinal * v;
      } else if (lado === "cred") {
        cred[trib] += sinal * v;
        d -= sinal * v;
      }
    });
    porBloco[b.id] = d;
  });

  return {
    deb,
    cred,
    porBloco,
    total: sum(BLOCOS_MANUAIS.map((b) => porBloco[b.id])),
  };
}

/** Monta um período (semana ou mês): linhas por tributo + lista "a pagar". */
function montarPeriodo(base, eff, st, iss, bruto, dev, liq) {
  const rows = TRIBUTOS.map((t) => ({
    k: TRIBUTO_NOME[t],
    t,
    debN: base[t].deb,
    credN: base[t].cred,
    debM: eff.deb[t],
    credM: eff.cred[t],
    deb: base[t].deb + eff.deb[t],
    cred: base[t].cred + eff.cred[t],
  }));
  const pay = [
    ["ICMS", rows[0].deb - rows[0].cred],
    ["COFINS", rows[3].deb - rows[3].cred],
    ["ICMS-ST + DIFAL", st],
    ["IPI", rows[1].deb - rows[1].cred],
    ["PIS", rows[2].deb - rows[2].cred],
    ["ISS", iss],
  ];
  return {
    rows,
    pay,
    total: sum(pay.map((p) => p[1])),
    st,
    iss,
    bruto,
    dev,
    liq,
    eff,
  };
}

/**
 * Computa semanas (1..4) + mês consolidado.
 * - weeksData: semanas geradas (dados das notas).
 * - lancamentosPorSemana: array paralelo com lançamentos manuais de cada semana.
 * Retorna { periodos, mes } onde periodos[i] = semana i (com carryIn/carryOut,
 * res, payC, totalC) e mes = consolidado (periodos.length como apuração própria
 * do mês, igual à planilha).
 */
export function computarApuracao(weeksData, lancamentosPorSemana) {
  const L = LANC_MES;
  const P = weeksData.map((w, i) =>
    montarPeriodo(
      {
        icms: { deb: w.icms_deb, cred: w.icms_cred },
        ipi: { deb: w.ipi_deb, cred: w.ipi_cred },
        pis: { deb: w.pis_deb, cred: w.pis_cred },
        cofins: { deb: w.cof_deb, cred: w.cof_cred },
      },
      calcEfeitos(lancamentosPorSemana[i]),
      w.st,
      0,
      w.vendas,
      w.dev,
      w.vendas - w.dev
    )
  );

  // Saldo credor da semana anterior: excedente de crédito de cada tributo
  // é usado na semana seguinte.
  const carry = zeroTrib();
  P.forEach((p) => {
    p.carryIn = 0;
    p.carryOut = 0;
    p.rows.forEach((r) => {
      r.carryIn = carry[r.t];
      const net = r.deb - r.cred - r.carryIn;
      r.res = Math.max(net, 0);
      r.carryOut = Math.max(-net, 0);
      carry[r.t] = r.carryOut;
      p.carryIn += r.carryIn;
      p.carryOut += r.carryOut;
    });
    const byk = { ICMS: 0, IPI: 1, PIS: 2, COFINS: 3 };
    p.payC = p.pay.map((x) =>
      byk[x[0]] === undefined ? [x[0], x[1]] : [x[0], p.rows[byk[x[0]]].res]
    );
    p.totalC = sum(p.payC.map((x) => x[1]));
  });

  const S = (k) => sum(weeksData.map((w) => w[k]));
  const MBRUTO = S("vendas") + L.servicos;
  const MDEV = S("dev");
  const MLIQ = MBRUTO - MDEV + L.ajuste_liquido;

  const me = { deb: zeroTrib(), cred: zeroTrib(), porBloco: {}, total: 0 };
  P.forEach((p) => {
    TRIBUTOS.forEach((t) => {
      me.deb[t] += p.eff.deb[t];
      me.cred[t] += p.eff.cred[t];
    });
    me.total += p.eff.total;
  });
  BLOCOS_MANUAIS.forEach((b) => {
    me.porBloco[b.id] = sum(P.map((p) => p.eff.porBloco[b.id]));
  });

  const mes = montarPeriodo(
    {
      icms: { deb: S("icms_deb"), cred: S("icms_cred") },
      ipi: { deb: S("ipi_deb"), cred: S("ipi_cred") + L.ipi_cred_devcompra },
      pis: {
        deb: S("pis_deb") + L.pis_deb_serv + L.pis_deb_adj,
        cred:
          S("pis_cred") +
          L.pis_cred_energia +
          L.pis_cred_aluguel +
          L.pis_cred_adj_rev +
          L.pis_cred_devcompra,
      },
      cofins: {
        deb: S("cof_deb") + L.cof_deb_serv + L.cof_deb_adj,
        cred:
          S("cof_cred") +
          L.cof_cred_energia +
          L.cof_cred_aluguel +
          L.cof_cred_adj_rev +
          L.cof_cred_devcompra,
      },
    },
    me,
    S("st"),
    L.iss,
    MBRUTO,
    MDEV,
    MLIQ
  );

  return { periodos: P, mes };
}
